/**
 * 增强的审核员服务
 * 连接三层审核系统，提供完整的审核功能
 */

import { API_CONFIG, STORAGE_KEYS } from '../config/api';
import type { 
  ReviewerDashboardData, 
  PendingReviewItem, 
  ReviewAction, 
  ReviewHistoryItem,
  PendingReviewsResponse,
  ReviewSubmissionResponse 
} from '../types/auditTypes';

class EnhancedReviewerService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem(STORAGE_KEYS.REVIEWER_TOKEN);
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * 获取审核员仪表板数据（连接三层审核系统）
   */
  async getDashboardData(): Promise<ReviewerDashboardData> {
    try {
      // 首先尝试获取真实数据
      const response = await this.makeRequest<{
        success: boolean;
        data: ReviewerDashboardData;
      }>('/api/simple-reviewer/dashboard');

      if (response.success) {
        return response.data;
      }
      
      throw new Error('Dashboard API returned unsuccessful response');
    } catch (error) {
      console.warn('Failed to fetch real dashboard data, using mock data:', error);
      
      // 返回模拟数据，但结构与三层审核系统一致
      return {
        stats: {
          total_pending: 12,
          today_completed: 8,
          total_completed: 156,
          average_review_time: 5.2,
          pending_by_level: {
            rule_flagged: 3,
            ai_flagged: 6,
            user_complaints: 3
          },
          pending_by_type: {
            story: 7,
            questionnaire: 3
          },
          pending_by_priority: {
            urgent: 2,
            high: 4,
            medium: 5,
            low: 1
          }
        },
        recent_activities: [
          {
            id: 1,
            content_type: 'story',
            audit_result: 'approved',
            created_at: new Date(Date.now() - 3600000).toISOString(),
            title: '学生就业故事分享',
            audit_level: 'manual_review'
          },
          {
            id: 2,
            content_type: 'questionnaire',
            audit_result: 'rejected',
            created_at: new Date(Date.now() - 7200000).toISOString(),
            title: '实习经验问卷',
            audit_level: 'ai_assisted'
          }
        ],
        performance_metrics: {
          approval_rate: 0.85,
          average_daily_reviews: 15.3,
          quality_score: 0.92
        }
      };
    }
  }

  /**
   * 获取待审核内容列表（按三层审核系统分类）
   */
  async getPendingReviews(params: {
    page?: number;
    pageSize?: number;
    audit_level?: 'rule_based' | 'ai_assisted' | 'manual_review';
    content_type?: 'story' | 'questionnaire';
    priority?: 'urgent' | 'high' | 'medium' | 'low';
    has_complaints?: boolean;
  } = {}): Promise<PendingReviewsResponse> {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });

      const response = await this.makeRequest<PendingReviewsResponse>(
        `/api/simple-reviewer/pending-reviews?${queryParams.toString()}`
      );

      return response;
    } catch (error) {
      console.warn('Failed to fetch pending reviews, using mock data:', error);
      
      // 返回模拟数据
      const mockItems: PendingReviewItem[] = [
        {
          id: 1,
          content_type: 'story',
          title: '我的实习经历分享 - 在科技公司的成长之路',
          content_preview: '在大三的暑假，我有幸在一家知名科技公司实习。这段经历让我学到了很多专业知识...',
          author: {
            user_id: 'user_001',
            username: '张同学',
            is_anonymous: false
          },
          audit_level: 'manual_review',
          priority: 'medium',
          submitted_at: new Date(Date.now() - 7200000).toISOString(),
          ai_audit_result: {
            passed: false,
            confidence: 0.65,
            risk_categories: ['potential_sensitive_content'],
            flagged_content: [
              {
                text: '公司内部信息',
                reason: '可能包含敏感信息',
                confidence: 0.7
              }
            ],
            model_version: 'v2.1',
            processing_time_ms: 150
          },
          tags: ['实习经验', '科技行业'],
          risk_score: 0.65,
          risk_factors: ['AI标记敏感内容', '包含公司信息']
        },
        {
          id: 2,
          content_type: 'story',
          title: '求职面试技巧总结',
          content_preview: '经过多次面试的经历，我总结了一些实用的面试技巧...',
          author: {
            user_id: 'user_002',
            username: '李同学',
            is_anonymous: false
          },
          audit_level: 'manual_review',
          priority: 'high',
          submitted_at: new Date(Date.now() - 14400000).toISOString(),
          complaint_info: {
            complaint_count: 2,
            complaint_reasons: ['内容不准确', '误导性信息'],
            latest_complaint_at: new Date(Date.now() - 3600000).toISOString()
          },
          tags: ['求职技巧', '面试经验'],
          risk_score: 0.8,
          risk_factors: ['用户投诉', '内容争议']
        }
      ];

      return {
        success: true,
        data: {
          reviews: mockItems,
          pagination: {
            total: mockItems.length,
            page: params.page || 1,
            pageSize: params.pageSize || 10,
            totalPages: Math.ceil(mockItems.length / (params.pageSize || 10))
          }
        }
      };
    }
  }

  /**
   * 提交审核结果（连接三层审核系统）
   */
  async submitReview(action: any): Promise<ReviewSubmissionResponse> {
    try {
      // 确保发送的数据格式与后端API期望的一致（驼峰命名）
      const requestBody = {
        auditId: action.auditId,
        action: action.action,
        reason: action.reason || '',
        notes: action.notes || ''
      };

      console.log('[ENHANCED_REVIEWER_SERVICE] Submitting review:', requestBody);

      const response = await this.makeRequest<ReviewSubmissionResponse>(
        '/api/simple-reviewer/submit-review',
        {
          method: 'POST',
          body: JSON.stringify(requestBody)
        }
      );

      console.log('[ENHANCED_REVIEWER_SERVICE] Review submission response:', response);
      return response;
    } catch (error) {
      console.error('[ENHANCED_REVIEWER_SERVICE] Failed to submit review:', error);

      // 不再返回模拟响应，直接抛出错误让调用方处理
      throw error;
    }
  }

  /**
   * 获取审核历史（包含完整的审核路径）
   */
  async getReviewHistory(params: {
    page?: number;
    pageSize?: number;
    startDate?: string;
    endDate?: string;
    decision?: 'approved' | 'rejected';
    content_type?: string;
  } = {}): Promise<{
    success: boolean;
    data?: {
      items: ReviewHistoryItem[];
      total: number;
      page: number;
      page_size: number;
    };
    error?: string;
  }> {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });

      const response = await this.makeRequest<any>(
        `/api/simple-reviewer/history?${queryParams.toString()}`
      );

      return response;
    } catch (error) {
      console.warn('Failed to fetch review history, using mock data:', error);
      
      // 返回模拟历史数据
      const mockHistory: ReviewHistoryItem[] = [
        {
          id: 1,
          content_type: 'story',
          title: '大学生创业经验分享',
          author: '陈同学',
          final_decision: 'approved',
          audit_path: ['rule_based', 'ai_assisted', 'manual_review'],
          manual_result: {
            decision: 'approved',
            reviewer_id: 'reviewer_001',
            reviewer_name: '审核员A',
            reason: '内容积极正面，对其他学生有启发意义',
            reviewed_at: new Date(Date.now() - 86400000).toISOString(),
            review_time_minutes: 3.5
          },
          submitted_at: new Date(Date.now() - 90000000).toISOString(),
          completed_at: new Date(Date.now() - 86400000).toISOString(),
          total_processing_time: 3600000, // 1小时
          review_quality_score: 0.95
        }
      ];

      return {
        success: true,
        data: {
          items: mockHistory,
          total: mockHistory.length,
          page: params.page || 1,
          page_size: params.pageSize || 10
        }
      };
    }
  }

  /**
   * 获取内容详情（包含完整的审核历史）
   */
  async getContentDetails(auditId: number): Promise<{
    success: boolean;
    data?: PendingReviewItem & {
      full_content: string;
      audit_history: Array<{
        level: string;
        result: any;
        timestamp: string;
      }>;
    };
    error?: string;
  }> {
    try {
      const response = await this.makeRequest<any>(
        `/api/simple-reviewer/content/${auditId}`
      );

      return response;
    } catch (error) {
      console.warn('Failed to fetch content details, using mock data:', error);
      
      return {
        success: false,
        error: 'Content details not available in mock mode'
      };
    }
  }
}

export const enhancedReviewerService = new EnhancedReviewerService();
export default enhancedReviewerService;
